import { Suspense, useContext, useEffect, useState } from "react";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import Loading from "../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { useNavigate } from "react-router-dom";
import {
  IPurchaseOrder,
  IPurchaseOrderItem,
  PURCHASEORDER_STATUS,
} from "../../../../types/purchaseOrder.types";
import {
  approvePurchaseOrder,
  getPurchaseOrders,
  rejectPurchaseOrder,
  cancelPurchaseOrder,
  revalidar,
} from "../../../../common/services/purchaseOrder.api";
import ModalCreatePurchaseOrder from "../modals/ModalCreatePurchaseOrder";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { useUser } from "../../../../common/services/users.api";
import ModalConfirmNfe from "../modals/ModalConfirmNfe";
import {
  getCompanies,
  getCompanyId,
} from "../../../../common/services/company.api";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import { FaInfoCircle } from "react-icons/fa";
import { numberToCurrency } from "../../../../common/helpers/currency.helpers";
import { Popover } from "flowbite-react";
import ModalConfirmStatus from "../modals/ModalConfirmStatus";
import { useCompanyStore } from "@/common/states/store/Company";

const STATUS_LABELS = {
  [PURCHASEORDER_STATUS.APPROVED]: "Aprovada",
  [PURCHASEORDER_STATUS.PRE_APPROVED]: "Pre-Aprovada",
  [PURCHASEORDER_STATUS.PENDING]: "Pendente",
  [PURCHASEORDER_STATUS.REJECTED]: "Reprovada",
  [PURCHASEORDER_STATUS.CANCELED]: "Cancelada",
  [PURCHASEORDER_STATUS.PENDING_RELEASE]: "Lançamento Pendente",
};

type StatusCounterPurchaseOrder = {
  [key in PURCHASEORDER_STATUS]?: number | string;
} & {
  totalGeral?: number | string;
};

function getStatusLabels(status: PURCHASEORDER_STATUS) {
  return STATUS_LABELS[status] || status;
}

function TablePurchaseOrder() {
  useContext(AuthContext);
  const companyId = getCompanyId();
  const companyStore = useCompanyStore();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [purchaseOrders, setPurchaseOrders] = useState<IPurchaseOrder[]>([]);
  const [statusCounter, setStatusCounter] =
    useState<StatusCounterPurchaseOrder>({});
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
  });
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const { notifySuccess, notifyError } = useToastContext();
  const [companies, setCompanies] = useState<CompanyAccess>([]);
  const user = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };

  type CompanyAccess = {
    companyId: string;
    companyName: string;
  }[];

  useEffect(() => {
    async function loadPurchaseOrders() {
      setIsLoading(true);
      const result = await getPurchaseOrders({
        page,
        orderBy: "-createdAt",
        status: filters.status,
        search: filters.search,
      });
      const resultCompanies = await getCompanies();
      setCompanies(resultCompanies.companies);
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      if (result.statusCounter) {
        setStatusCounter(result.statusCounter as string as unknown as StatusCounterPurchaseOrder);
      }
      setPurchaseOrders(result.data);
      setIsLoading(false);
    }
    loadPurchaseOrders();
  }, [companyId, data.itemsPerPage, filters, page, companyStore]);

  const handleApprovedPurchaseOrder = async (_id: string) => {
    const result = await approvePurchaseOrder({
      _id: _id,
      requestorEmail: user.email as string,
      requestorName: user.name as string,
    });
    cleanActionData();
    if (!result.ok) {
      notifyError(result.message as string);
      return;
    }

    if (result.data.status === PURCHASEORDER_STATUS.APPROVED) {
      notifySuccess("Ordem de Compra Pré-Aprovada com sucesso");
      setFilters({ ...filters });
      return;
    }

    notifySuccess("Ordem de Compra Aprovada com sucesso");
  };

  const handleReprovedPurchaseOrder = async (data: IPurchaseOrder) => {
    const result = await rejectPurchaseOrder(data._id as string);
    if (result.ok) {
      notifySuccess("Reprovado com Sucesso a Ordem de Compra");
      setFilters({ ...filters });
      cleanActionData();
      return;
    }
    if (!result.ok) {
      notifyError(result.message as string);
    }
  };

  const handleRemovePurchaseOrder = async (data: IPurchaseOrder) => {
    const result = await cancelPurchaseOrder(data._id as string);
    if (result.ok) {
      notifySuccess("Removido com Sucesso a Ordem de Compra");
      setFilters({ ...filters });
      cleanActionData();
      return;
    }
    if (!result.ok) {
      notifyError(result.message as string);
    }
  };

  /*const actionPostInventory = async (data: IPurchaseOrderItem) => {
    setAction({ eventName: 'postInventory', data: data })
  }*/

  const actions = [
    {
      label: "Visualizar",
      permission: "edit:inventory-pieces",
      action: (data: unknown) => {
        const order = data as IPurchaseOrder;
        navigate(`/compras/ordem/${order._id}`);
      },
    },
    {
      label: "Aprovar",
      action: (data) => {
        setAction({ eventName: "approve", data });
      },
      permission: "edit:inventory-pieces",
      disabled: (data) => {
        return (
          (data.status == "approved" || getCompanyId() == "*" ? true : false) ||
          data?.requestorName == user.name
        );
      },
      visible: (data) => {
        return (
          (data.status == "pending" || data.status == "pre-approved") &&
          getCompanyId() != "*"
        );
      },
    },
    {
      label: "Reprovar",
      action: (data) => {
        setAction({ eventName: "reprove", data });
      },
      permission: "edit:inventory-pieces",
      visible: () => {
        return getCompanyId() != "*";
      },
    },
    {
      label: "Remover",
      action: (data) => {
        setAction({ eventName: "remove", data });
      },
      permission: "edit:inventory-pieces",
      visible: () => {
        return getCompanyId() != "*";
      },
    },
    {
      label: "Revalidar",
      action: (data) => {
        (async function () {
          const result = await revalidar(data._id);
          if (!result.ok) {
            notifyError(result.message as string);
          } else {
            notifySuccess("Revalidado com sucesso");
          }
          setAction({ eventName: "revalidate", data });
        })();
      },
      permission: "edit:inventory-pieces",
      visible: (data) => {
        return getCompanyId() != "*" && data.status == "canceled";
      },
    },
    /*{
      label: "Lançar Estoque",
      permission: "edit:inventory-pieces",
      action: (data) => actionPostInventory(data),
      visible: (data) => {
        return (PURCHASEORDER_STATUS.PENDING_RELEASE && !data.inventoryLaunched && getCompanyId() != "*")
      }
    },*/
  ];

  const formatStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    if (data.status == "approved") {
      return <span className="text-greenDark font-semibold">{content}</span>;
    } else if (data.status == "pending") {
      return <span className="text-bluePrimary font-semibold">{content}</span>;
    } else if (data.status == "rejected") {
      return <span className="text-redPrimary font-semibold">{content}</span>;
    } else if (data.status == "pending-release") {
      return <span className="text-black font-semibold">{content}</span>;
    } else if (data.status == "pre-approved") {
      return (
        <span className="text-yellowPrimary font-semibold">{content}</span>
      );
    } else if (data.status == "canceled") {
      return <span className="text-gray">{content}</span>;
    }
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Ordem de Compras"
          tableData={purchaseOrders}
          placeholder="Digite o fornecedor ou o código da ordem de compra"
          statusOptions={STATUS_LABELS}
          newItemLabel="Nova Ordem de Compra"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="inventory-pieces"
        />
        {!isLoading && (
          <div className="grid grid-cols-2 gap-3 mt-1 mb-3 lg:grid-cols-4">
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">
                Total de Ordens
              </h2>
              <p className="text-lg text-white w-1/2 text-right">
                {statusCounter.totalGeral || 0}
              </p>
            </div>

            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">Aprovadas</h2>
              <p className="text-lg text-white w-1/2 text-right">
                {statusCounter[PURCHASEORDER_STATUS.APPROVED] || 0}
              </p>
            </div>

            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">
                Pré-Aprovadas
              </h2>
              <p className="text-lg text-white w-1/2 text-right">
                {statusCounter[PURCHASEORDER_STATUS.PRE_APPROVED] || 0}
              </p>
            </div>

            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">Pendentes</h2>
              <p className="text-lg text-white w-1/2 text-right">
                {statusCounter[PURCHASEORDER_STATUS.PENDING] || 0}
              </p>
            </div>
          </div>
        )}
        <GenericTable
          headers={{
            createdAt: "Data",
            companyId: "Filial",
            code: "Código",
            requestorName: "Solicitante",
            supplierName: "Fornecedor",
            status: "Status",
            financial: "Valor",
          }}
          entity="purchase-order"
          actions={actions}
          data={purchaseOrders}
          renders={{
            createdAt: (createdAt, purchaseOrder) => {
              const formattedDate = formatDateAndTime(createdAt);
              return formatStyle(
                <span
                  className="flex items-center gap-2"
                  style={{ fontSize: "15px" }}
                >
                  {formattedDate || ""}
                </span>,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            code: (code, purchaseOrder) => {
              return formatStyle(
                <a
                  href={`compras/ordem/${(purchaseOrder as IPurchaseOrder)._id
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/compras/ordem/${(purchaseOrder as IPurchaseOrder)._id}`
                    );
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span style={{ fontWeight: 600 }}>{code as string}</span>
                </a>,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            requestorName: (requestorName, purchaseOrder) => {
              return formatStyle(
                requestorName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            supplierName: (supplierName, purchaseOrder) => {
              return formatStyle(
                supplierName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            financial: (data: unknown, context: unknown) => {
              const financial = data as { value: string };
              const purchaseOrder = context as IPurchaseOrder;

              return formatStyle(
                <div className="flex items-center gap-2">
                  {purchaseOrder.description && (
                    <Popover
                      content={
                        <div className="bg-white text-black border border-gray-300 rounded-md shadow-md p-2 max-w-sm z-50">
                          <span className="text-sm">
                            {purchaseOrder.description}
                          </span>
                        </div>
                      }
                      placement="top"
                      trigger="click"
                    >
                      <button className="cursor-pointer text-blue-600">
                        <FaInfoCircle />
                      </button>
                    </Popover>
                  )}
                  <span>{numberToCurrency(financial?.value)}</span>
                </div>,
                purchaseOrder
              ) as React.ReactElement;
            },
            status: (status, purchaseOrder) => {
              return formatStyle(
                getStatusLabels(status as PURCHASEORDER_STATUS),
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            votesApproved: (votesApproved, purchaseOrder) => {
              if ((votesApproved as { requestorName: string }[])?.length) {
                return formatStyle(
                  (votesApproved as { requestorName: string }[])[0]
                    .requestorName,
                  purchaseOrder as IPurchaseOrder
                ) as React.ReactElement;
              } else {
                return formatStyle(
                  "Pendente",
                  purchaseOrder as IPurchaseOrder
                ) as React.ReactElement;
              }
            },
            companyId: (companyId, purchaseOrder) => {
              if (!companies) {
                return "";
              }
              return formatStyle(
                companies.find((company) => company.companyId === companyId)
                  ?.companyName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
          }}
        />
        {isAction("postInventory") && (
          <ModalConfirmNfe
            purchaseOrderId={(action.data as IPurchaseOrderItem)._id as string}
            onClose={cleanActionData}
          ></ModalConfirmNfe>
        )}
        {isLoading && <Loading />}

        {isAction("approve") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Aprovação"
            description="Tem certeza que deseja aprovar esta ordem de compra?"
            confirmLabel="Sim, aprovar"
            cancelLabel="Cancelar"
            confirmType="approve"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleApprovedPurchaseOrder(
                (action.data as IPurchaseOrder)._id as string
              )
            }
          />
        )}

        {isAction("reprove") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Reprovação"
            description="Tem certeza que deseja reprovar esta ordem de compra?"
            confirmLabel="Sim, reprovar"
            cancelLabel="Cancelar"
            confirmType="reject"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleReprovedPurchaseOrder(action.data as IPurchaseOrder)
            }
          />
        )}

        {isAction("remove") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Remoção"
            description="Tem certeza que deseja remover esta ordem de compra?"
            confirmLabel="Sim, remover"
            cancelLabel="Cancelar"
            confirmType="remove"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleRemovePurchaseOrder(action.data as IPurchaseOrder)
            }
          />
        )}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={data.itemsPerPage || 10}
          goToPage={setPage}
        />
      </div>
      {isAction("create") && (
        <ModalCreatePurchaseOrder onClose={cleanActionData} />
      )}
    </Suspense>
  );
}

export default TablePurchaseOrder;
